/**
 * Online/Offline-Status-Indicator + Outbox-Counter.
 *
 * Update-Mechanismus: nur via onOutboxChange (push), kein Polling.
 * Sync-Heartbeat (in lib/sync.ts) ruft notify() bei jedem Check.
 */
import { useEffect, useState } from 'react';
import { isOnline, onOutboxChange, syncOutbox } from '../lib/sync';
import { outboxCount, outboxBlockedCount } from '../lib/db';

export function OnlineBadge() {
  const [online, setOnline] = useState(() => isOnline());
  const [pending, setPending] = useState(0);
  const [blocked, setBlocked] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function refreshCount() {
      const [c, b] = await Promise.all([outboxCount(), outboxBlockedCount()]);
      if (!mounted) return;
      setPending(c);
      setBlocked(b);
    }
    void refreshCount();
    const offChange = onOutboxChange(refreshCount);
    function onlineHandler() {
      setOnline(true);
      void refreshCount();
    }
    function offlineHandler() {
      setOnline(false);
    }
    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);
    return () => {
      mounted = false;
      offChange();
      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', offlineHandler);
    };
  }, []);

  async function manualSync() {
    setBusy(true);
    try {
      await syncOutbox();
    } finally {
      setBusy(false);
    }
  }

  if (online && pending === 0) {
    return (
      <span className="online-badge online-badge--ok">
        <span className="online-badge__dot" /> ONLINE
      </span>
    );
  }
  if (!online) {
    return (
      <span className="online-badge online-badge--offline">
        <span className="online-badge__dot" /> OFFLINE
        {pending > 0 ? ` · ${pending} WARTEN` : ''}
      </span>
    );
  }
  // online + pending: orange, klickbar
  const blockedSuffix = blocked > 0 ? ` (${blocked} blockiert)` : '';
  return (
    <button
      type="button"
      onClick={manualSync}
      disabled={busy}
      className="online-badge online-badge--pending"
      title={blocked > 0 ? `${blocked} Lead(s) brauchen manuelle Prüfung` : ''}
    >
      <span className="online-badge__dot" /> {busy ? 'SYNCT…' : `${pending} SYNCEN${blockedSuffix}`}
    </button>
  );
}
